import React from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../Typography";
import { IDEA_FLOW_STAGE_NAMES } from "../../Container/IdeasReview/utils";

const ProgressTimeline = ({
  stages = [],
  currentStage = 0, // the stage the user is currently viewing (selection highlight)
  maxAccessibleStage = null, // the idea's actual progress; drives completed / current / locked
  daysRemaining = null,
  onStageClick = null,
}) => {
  const defaultStages = IDEA_FLOW_STAGE_NAMES.map((name, id) => ({ name, id }));
  const stagesToDisplay = stages.length > 0 ? stages : defaultStages;
  // The idea's actual progress (frontier). Completed/current/locked states are
  // derived from THIS — not from the viewed stage — so navigating back to an
  // earlier stage never makes later, already-reached stages look locked again.
  // Falls back to the viewed stage when a caller doesn't supply progress.
  const frontier =
    maxAccessibleStage != null
      ? Math.min(maxAccessibleStage, stagesToDisplay.length - 1)
      : currentStage;
  const maxStage = frontier;

  return (
    <div className="w-full mb-2 px-2 sm:px-4">
      <div className="relative flex items-start justify-between">
        {/* Connector lines sit behind the nodes, centred on the circles */}
        <div
          className="absolute z-0 flex"
          style={{ top: 13, left: "10%", right: "10%", height: 2 }}
        >
          {stagesToDisplay.slice(0, -1).map((stage, index) => {
            const filled = index < frontier;
            return (
              <div
                key={`line-${index}`}
                className={`flex-1 h-0.5 mx-1 rounded-full ${
                  filled ? "bg-violet-600" : "bg-slate-200"
                }`}
              />
            );
          })}
        </div>

        {stagesToDisplay.map((stage, index) => {
          const stageName = stage.name || IDEA_FLOW_STAGE_NAMES[index];
          const isActive = index === frontier; // the idea's current stage
          const isCompleted = index < frontier;
          const isLocked = index > maxStage; // beyond the frontier
          const isSelected = index === currentStage; // the stage being viewed
          const isClickable = onStageClick !== null && !isLocked;

          return (
            <div
              key={stage.id ?? index}
              className={`flex flex-col items-center relative z-10 flex-1 min-w-0 ${
                isClickable
                  ? "cursor-pointer"
                  : isLocked
                  ? "cursor-not-allowed"
                  : ""
              }`}
              onClick={() => {
                if (isClickable && onStageClick) onStageClick(index);
              }}
              title={
                isLocked
                  ? "Your mentor will unlock this stage after approving the previous one"
                  : `Open ${stageName}`
              }
            >
              {/* Node */}
              {isCompleted ? (
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full bg-violet-600 ${
                    isSelected ? "ring-2 ring-violet-300 ring-offset-2" : ""
                  }`}
                >
                  <Icon icon="lucide:check" className="h-4 w-4 text-white" />
                </div>
              ) : isActive ? (
                <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-violet-600 bg-white ring-4 ring-violet-200/70">
                  <span className="h-2.5 w-2.5 rounded-full bg-violet-600" />
                </div>
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                </div>
              )}

              {/* Label */}
              <div className="mt-3 flex flex-col items-center gap-0.5 px-1 text-center">
                <Typography
                  as="p"
                  className={`text-sm leading-tight rounded-md px-2 py-0.5 transition-colors ${
                    isActive
                      ? "font-semibold text-primary-900"
                      : isSelected
                      ? "bg-violet-100 font-semibold text-violet-700"
                      : isCompleted
                      ? "font-medium text-primary-900"
                      : "font-medium text-tertiary-600"
                  }`}
                >
                  {stageName}
                </Typography>
                {isActive && daysRemaining != null ? (
                  <span className="text-xs font-medium text-violet-600">
                    {daysRemaining} days remaining
                  </span>
                ) : isCompleted ? (
                  <span className="text-xs text-tertiary-600">Complete</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressTimeline;
